/* v3.6u — Daily Summary
   End-of-day recap: m³ delivered · invoiced value · wait-time revenue · fleet
   health. Date-pickable so bookkeepers can walk back through the week. PDF
   button hits GET /api/admin/daily-summary.pdf for a print-ready copy. */
import { useEffect, useMemo, useState } from "react";
import { api, apiHref } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Layers, DollarSign, Timer, Wrench, Download, ChevronLeft, ChevronRight,
  RefreshCcw, TrendingUp, CheckCircle2, AlertTriangle, ShoppingCart,
} from "lucide-react";
import { API_BASE } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function shiftDay(iso, delta) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
}
function todayLocal() { return new Date().toISOString().slice(0, 10); }

export default function DailySummary() {
  const [date, setDate] = useState(() => new URLSearchParams(window.location.search).get("date") || todayLocal());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/daily-summary", { params: { date } });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load summary");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = () => {
    const url = apiHref(`/admin/daily-summary.pdf?date=${date}`);
    // Force a new tab so the browser handles the auth cookie + native download UX
    window.open(url, "_blank");
  };

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="daily-summary-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" />End of Day
          </div>
          <h1 className="heading text-2xl font-black uppercase mt-1">Daily Summary</h1>
          <p className="text-xs text-muted-industrial mt-1">
            One-page recap of everything that ran through the plant today. Save the PDF for weekly bookkeeping.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setDate(shiftDay(date, -1))}
            data-testid="ds-prev-day"
            className="rounded-none border-industrial bg-transparent uppercase text-xs h-9"
          ><ChevronLeft className="w-3 h-3" /></Button>
          <Input
            data-testid="ds-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-none bg-transparent border-industrial mono h-9 w-40"
          />
          <Button
            variant="outline"
            onClick={() => setDate(shiftDay(date, 1))}
            disabled={date >= todayLocal()}
            data-testid="ds-next-day"
            className="rounded-none border-industrial bg-transparent uppercase text-xs h-9"
          ><ChevronRight className="w-3 h-3" /></Button>
          <Button
            variant="outline"
            onClick={() => setDate(todayLocal())}
            data-testid="ds-today"
            className="rounded-none border-industrial bg-transparent uppercase text-xs h-9 tracking-widest"
          >Today</Button>
          <Button
            variant="outline"
            onClick={load}
            data-testid="ds-refresh"
            className="rounded-none border-industrial bg-transparent uppercase text-xs h-9 tracking-widest"
          ><RefreshCcw className="w-3 h-3 mr-1" />Refresh</Button>
          <Button
            data-testid="ds-download-pdf"
            onClick={downloadPdf}
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black uppercase text-xs h-9 tracking-widest font-bold"
          ><Download className="w-3.5 h-3.5 mr-1" />Download Daily PDF</Button>
        </div>
      </div>

      {/* Hero stat tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          testId="stat-m3"
          icon={Layers}
          label="M³ Poured Today"
          value={loading || !data ? "—" : `${data.m3_delivered}`}
          suffix="m³"
          hint={data && `${data.m3_ordered} m³ ordered · ${data.dockets} dockets`}
        />
        <StatTile
          testId="stat-invoiced"
          icon={DollarSign}
          label="Invoiced (Completed)"
          value={loading || !data ? "—" : money(data.invoiced_total)}
          hint={data && `${data.invoices} invoice${data.invoices === 1 ? "" : "s"} · ${money(data.invoices_outstanding)} outstanding`}
          accent="orange"
        />
        <StatTile
          testId="stat-wait"
          icon={Timer}
          label="Wait-Time Revenue"
          value={loading || !data ? "—" : money(data.wait_time_revenue)}
          hint={data && (data.wait_time_revenue > 0 ? "Surcharges collected today" : "No wait-time charges — clean day")}
          accent={data && data.wait_time_revenue > 0 ? "orange" : "muted"}
        />
        <StatTile
          testId="stat-fleet"
          icon={Wrench}
          label="Fleet Issues"
          value={loading || !data ? "—" : `${data.fleet_issues}`}
          hint={data && (data.fleet_issues ? "See faults panel below" : "All trucks green — no issues logged")}
          accent={data && data.fleet_issues > 0 ? "red" : "green"}
        />
      </div>

      {/* Secondary totals row */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <MiniStat label="EFT Sales" value={money(data.cash_sale_total)} icon={ShoppingCart} />
          <MiniStat label="Top-ups" value={data.topups} icon={TrendingUp} />
          <MiniStat label="Jobs Today" value={data.jobs} icon={CheckCircle2} />
          <MiniStat label="Wait-time Loads" value={data.dockets_list.filter((d) => Number(d.wait_charge || 0) > 0).length} icon={Timer} />
        </div>
      )}

      {/* Fleet Health panel */}
      <div className="border border-industrial bg-surface" data-testid="daily-fleet-panel">
        <div className="p-3 border-b border-industrial flex items-center gap-2">
          <Wrench className={`w-4 h-4 ${data && data.fleet_issues ? "text-red-500" : "text-emerald-500"}`} />
          <div className="label-tag">Fleet Health · Faults &amp; Maintenance</div>
          <div className="ml-auto mono text-[10px] text-muted-industrial">
            {data ? `${data.fleet_issues} item${data.fleet_issues === 1 ? "" : "s"}` : ""}
          </div>
        </div>
        {data && data.fleet_list.length === 0 ? (
          <div className="p-6 text-center text-emerald-500 mono text-xs uppercase tracking-widest flex items-center gap-2 justify-center">
            <CheckCircle2 className="w-4 h-4" /> All trucks green — no issues reported today
          </div>
        ) : (
          <div className="overflow-x-auto max-h-72">
            <table className="w-full mono text-xs">
              <thead className="bg-surface-2 sticky top-0">
                <tr className="border-b border-industrial">
                  <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Truck</th>
                  <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Status</th>
                  <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Issue</th>
                  <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Reported</th>
                </tr>
              </thead>
              <tbody>
                {(data?.fleet_list || []).map((f, i) => (
                  <tr key={i} className="border-b border-industrial" data-testid={`fleet-row-${i}`}>
                    <td className="p-2 font-bold">#{f.truck_number}</td>
                    <td className="p-2 uppercase text-[10px] text-red-400">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      {(f.status || "").replace("_", " ")}
                    </td>
                    <td className="p-2 text-muted-industrial">{f.note || "—"}</td>
                    <td className="p-2 text-muted-industrial tabular-nums">{(f.reported_at || "").slice(0, 16)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dockets table */}
      <div className="border border-industrial bg-surface" data-testid="daily-dockets-panel">
        <div className="p-3 border-b border-industrial flex items-center gap-2">
          <Layers className="w-4 h-4 text-primary-orange" />
          <div className="label-tag">Dockets Today</div>
          <div className="ml-auto mono text-[10px] text-muted-industrial">
            {data ? `${data.dockets_list.length} row${data.dockets_list.length === 1 ? "" : "s"}` : ""}
          </div>
        </div>
        <div className="overflow-x-auto max-h-[420px]">
          <table className="w-full mono text-xs">
            <thead className="bg-surface-2 sticky top-0">
              <tr className="border-b border-industrial">
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Docket #</th>
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Customer</th>
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Mix</th>
                <th className="p-2 text-right uppercase text-[9px] tracking-widest text-muted-industrial">m³</th>
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Truck</th>
                <th className="p-2 text-right uppercase text-[9px] tracking-widest text-muted-industrial">Wait $</th>
                <th className="p-2 text-left uppercase text-[9px] tracking-widest text-muted-industrial">Status</th>
              </tr>
            </thead>
            <tbody>
              {data && data.dockets_list.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-industrial">No dockets on this day.</td></tr>
              )}
              {(data?.dockets_list || []).map((d, i) => (
                <tr key={i} className="border-b border-industrial hover:bg-surface-2" data-testid={`docket-row-${i}`}>
                  <td className="p-2 tabular-nums">{d.docket_no}</td>
                  <td className="p-2 truncate max-w-[200px]" title={d.customer_name}>{d.customer_name || "—"}</td>
                  <td className="p-2 text-primary-orange">{d.mix_code}</td>
                  <td className="p-2 text-right tabular-nums">{d.quantity_m3}</td>
                  <td className="p-2 text-primary-orange">#{d.truck_number}</td>
                  <td className={`p-2 text-right tabular-nums ${d.wait_charge > 0 ? "text-primary-orange font-bold" : "text-muted-industrial"}`}>
                    {d.wait_charge > 0 ? money(d.wait_charge) : "—"}
                  </td>
                  <td className="p-2 text-muted-industrial uppercase text-[10px]">{(d.status || "").replace("_", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, suffix, hint, accent, testId }) {
  const accentBg = {
    orange: "border-primary-orange/50 bg-primary-orange/5",
    red:    "border-red-500/60 bg-red-500/10",
    green:  "border-emerald-500/50 bg-emerald-500/5",
    muted:  "border-industrial",
  }[accent] || "border-industrial bg-surface";
  const valueColor = {
    orange: "text-primary-orange",
    red: "text-red-400",
    green: "text-emerald-400",
  }[accent] || "text-ink";
  return (
    <div className={`border ${accentBg} p-4`} data-testid={testId}>
      <div className="flex items-center gap-1.5 label-tag">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`mono text-4xl font-black leading-none mt-2 tabular-nums ${valueColor}`}>
        {value}{suffix && <span className="text-lg text-muted-industrial ml-1">{suffix}</span>}
      </div>
      {hint && <div className="text-[11px] text-muted-industrial mt-2 mono">{hint}</div>}
    </div>
  );
}

function MiniStat({ label, value, icon: Icon }) {
  return (
    <div className="border border-industrial bg-surface px-3 py-2 flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-muted-industrial" />
      <div className="flex-1">
        <div className="label-tag">{label}</div>
        <div className="mono font-bold text-ink tabular-nums">{value}</div>
      </div>
    </div>
  );
}
